import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import {
  Plus,
  Trash2,
  Edit,
  X,
  Loader2,
  AlertCircle,
  Search,
  Calendar,
  Clock,
  Users,
  BookOpen,
  ClipboardCheck,
  FileText,
  ChevronLeft,
  ChevronRight,
  Check,
  XCircle,
  UserMinus,
} from 'lucide-react';

// ===== Types =====

interface SchoolYear {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
  classCount: number;
}

interface SchoolClass {
  id: string;
  schoolYearId: string;
  name: string;
  description: string | null;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  room: string | null;
  maxStudents: number | null;
  gradeLevel: string | null;
  tuitionAmount: number | null;
  teacherCount: number;
  enrollmentCount: number;
  teachers: Array<{
    id: string;
    memberId: string;
    role: string;
    memberName: string | null;
  }>;
}

interface ClassDetail extends SchoolClass {
  schoolYear: SchoolYear;
  enrollments: Array<{
    id: string;
    memberId: string;
    status: string;
    enrolledAt: string;
    memberName: string | null;
  }>;
}

interface Enrollment {
  id: string;
  memberId: string;
  status: string;
  enrolledAt: string;
  member: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
    dateOfBirth: string | null;
  } | null;
}

interface AttendanceRecord {
  id: string;
  classId: string;
  memberId: string;
  date: string;
  status: string;
  notes: string | null;
  memberName: string | null;
}

interface AttendanceReport {
  totalClasses: number;
  students: Array<{
    memberId: string;
    memberName: string | null;
    totalClasses: number;
    present: number;
    absent: number;
    late: number;
    excused: number;
    attendancePercentage: number;
  }>;
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ===== Main Component =====

export function SchoolPage() {
  const [activeTab, setActiveTab] = useState<'years' | 'classes' | 'attendance' | 'reports'>('years');

  const tabs = [
    { key: 'years' as const, label: 'School Years', icon: Calendar },
    { key: 'classes' as const, label: 'Classes', icon: BookOpen },
    { key: 'attendance' as const, label: 'Attendance', icon: ClipboardCheck },
    { key: 'reports' as const, label: 'Reports', icon: FileText },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Religious School</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage school years, classes, enrollment, and attendance
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium',
                activeTab === tab.key
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'years' && <YearsTab />}
      {activeTab === 'classes' && <ClassesTab />}
      {activeTab === 'attendance' && <AttendanceTab />}
      {activeTab === 'reports' && <ReportsTab />}
    </div>
  );
}

// ===== School Years Tab =====

function YearsTab() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingYear, setEditingYear] = useState<SchoolYear | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: years, isLoading } = useQuery({
    queryKey: ['school-years'],
    queryFn: async () => {
      const response = await api.get<SchoolYear[]>('/school/years');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/school/years/${id}`);
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-years'] });
      setDeleteConfirm(null);
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'planning': return 'bg-yellow-100 text-yellow-700';
      case 'completed': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">School Years</h2>
        <button
          onClick={() => { setEditingYear(null); setShowModal(true); }}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          Add School Year
        </button>
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Classes</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {(!years || years.length === 0) ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  No school years yet. Create one to get started.
                </td>
              </tr>
            ) : (
              years.map((year) => (
                <tr key={year.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{year.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(year.startDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(year.endDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('inline-flex rounded-full px-2 py-1 text-xs font-medium', getStatusColor(year.status))}>
                      {year.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{year.classCount}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => { setEditingYear(year); setShowModal(true); }}
                        className="p-1 text-gray-400 hover:text-primary-600"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(year.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <SchoolYearModal
          year={editingYear}
          onClose={() => { setShowModal(false); setEditingYear(null); }}
          onSaved={() => {
            setShowModal(false);
            setEditingYear(null);
            queryClient.invalidateQueries({ queryKey: ['school-years'] });
          }}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete School Year</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== School Year Modal =====

function SchoolYearModal({
  year,
  onClose,
  onSaved,
}: {
  year: SchoolYear | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [formData, setFormData] = useState({
    name: year?.name || '',
    startDate: year?.startDate ? new Date(year.startDate).toISOString().split('T')[0] : '',
    endDate: year?.endDate ? new Date(year.endDate).toISOString().split('T')[0] : '',
    status: year?.status || 'planning',
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (year) {
        const response = await api.patch(`/school/years/${year.id}`, formData);
        if (!response.success) throw new Error(response.error?.message);
      } else {
        const response = await api.post('/school/years', formData);
        if (!response.success) throw new Error(response.error?.message);
      }
    },
    onSuccess: () => onSaved(),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{year ? 'Edit' : 'Add'} School Year</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., 2026-2027"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !formData.name || !formData.startDate || !formData.endDate}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving...' : year ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Classes Tab =====

function ClassesTab() {
  const queryClient = useQueryClient();
  const [selectedYearId, setSelectedYearId] = useState('');
  const [showClassModal, setShowClassModal] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: years } = useQuery({
    queryKey: ['school-years'],
    queryFn: async () => {
      const response = await api.get<SchoolYear[]>('/school/years');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const { data: classes, isLoading } = useQuery({
    queryKey: ['school-classes', selectedYearId],
    queryFn: async () => {
      const params = selectedYearId ? `?schoolYearId=${selectedYearId}` : '';
      const response = await api.get<SchoolClass[]>(`/school/classes${params}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/school/classes/${id}`);
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-classes'] });
      setDeleteConfirm(null);
      if (selectedClassId === deleteConfirm) setSelectedClassId(null);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <select
            value={selectedYearId}
            onChange={(e) => setSelectedYearId(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">All School Years</option>
            {years?.map((y) => (
              <option key={y.id} value={y.id}>{y.name}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => setShowClassModal(true)}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          Add Class
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Day</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Room</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teacher(s)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enrolled</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(!classes || classes.length === 0) ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No classes found. Create one to get started.
                  </td>
                </tr>
              ) : (
                classes.map((cls) => (
                  <tr
                    key={cls.id}
                    className={cn('hover:bg-gray-50 cursor-pointer', selectedClassId === cls.id && 'bg-primary-50')}
                    onClick={() => setSelectedClassId(selectedClassId === cls.id ? null : cls.id)}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{cls.name}</p>
                        {cls.gradeLevel && <p className="text-xs text-gray-500">{cls.gradeLevel}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{DAY_NAMES[cls.dayOfWeek]}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{cls.startTime} - {cls.endTime}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{cls.room || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {cls.teachers.map((t) => t.memberName || 'Unknown').join(', ') || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {cls.enrollmentCount}{cls.maxStudents ? `/${cls.maxStudents}` : ''}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirm(cls.id); }}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Class Detail Panel */}
      {selectedClassId && (
        <ClassDetailPanel classId={selectedClassId} />
      )}

      {showClassModal && (
        <ClassModal
          years={years || []}
          onClose={() => setShowClassModal(false)}
          onSaved={() => {
            setShowClassModal(false);
            queryClient.invalidateQueries({ queryKey: ['school-classes'] });
          }}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete Class</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Class Modal =====

function ClassModal({
  years,
  onClose,
  onSaved,
}: {
  years: SchoolYear[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [formData, setFormData] = useState({
    schoolYearId: years[0]?.id || '',
    name: '',
    description: '',
    dayOfWeek: 0,
    startTime: '09:00',
    endTime: '10:30',
    room: '',
    maxStudents: '',
    gradeLevel: '',
    tuitionAmount: '',
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/school/classes', {
        schoolYearId: formData.schoolYearId,
        name: formData.name,
        description: formData.description || undefined,
        dayOfWeek: formData.dayOfWeek,
        startTime: formData.startTime,
        endTime: formData.endTime,
        room: formData.room || undefined,
        maxStudents: formData.maxStudents ? parseInt(formData.maxStudents) : undefined,
        gradeLevel: formData.gradeLevel || undefined,
        tuitionAmount: formData.tuitionAmount ? parseFloat(formData.tuitionAmount) : undefined,
      });
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => onSaved(),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Add Class</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">School Year *</label>
            <select
              value={formData.schoolYearId}
              onChange={(e) => setFormData({ ...formData, schoolYearId: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {years.map((y) => (
                <option key={y.id} value={y.id}>{y.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Class Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Aleph Class"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Day *</label>
              <select
                value={formData.dayOfWeek}
                onChange={(e) => setFormData({ ...formData, dayOfWeek: parseInt(e.target.value) })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {DAY_NAMES.map((day, i) => (
                  <option key={i} value={i}>{day}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start *</label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End *</label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Room</label>
              <input
                type="text"
                value={formData.room}
                onChange={(e) => setFormData({ ...formData, room: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Students</label>
              <input
                type="number"
                value={formData.maxStudents}
                onChange={(e) => setFormData({ ...formData, maxStudents: e.target.value })}
                min="1"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level</label>
              <input
                type="text"
                value={formData.gradeLevel}
                onChange={(e) => setFormData({ ...formData, gradeLevel: e.target.value })}
                placeholder="e.g., K-2"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tuition ($)</label>
              <input
                type="number"
                value={formData.tuitionAmount}
                onChange={(e) => setFormData({ ...formData, tuitionAmount: e.target.value })}
                min="0"
                step="0.01"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !formData.name || !formData.schoolYearId}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
          >
            {mutation.isPending ? 'Creating...' : 'Create Class'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Class Detail Panel =====

function ClassDetailPanel({ classId }: { classId: string }) {
  const queryClient = useQueryClient();
  const [showAddTeacher, setShowAddTeacher] = useState(false);
  const [showEnrollStudent, setShowEnrollStudent] = useState(false);
  const [teacherSearch, setTeacherSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');

  const { data: classDetail, isLoading } = useQuery({
    queryKey: ['school-class', classId],
    queryFn: async () => {
      const response = await api.get<ClassDetail>(`/school/classes/${classId}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const { data: teacherResults } = useQuery({
    queryKey: ['member-search', teacherSearch],
    queryFn: async () => {
      const response = await api.get<Member[]>(`/members?search=${encodeURIComponent(teacherSearch)}&limit=10`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
    enabled: teacherSearch.length >= 2,
  });

  const { data: studentResults } = useQuery({
    queryKey: ['member-search-student', studentSearch],
    queryFn: async () => {
      const response = await api.get<Member[]>(`/members?search=${encodeURIComponent(studentSearch)}&limit=10`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
    enabled: studentSearch.length >= 2,
  });

  const addTeacherMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const response = await api.post(`/school/classes/${classId}/teachers`, { memberId });
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-class', classId] });
      queryClient.invalidateQueries({ queryKey: ['school-classes'] });
      setShowAddTeacher(false);
      setTeacherSearch('');
    },
  });

  const removeTeacherMutation = useMutation({
    mutationFn: async (teacherId: string) => {
      const response = await api.delete(`/school/classes/${classId}/teachers/${teacherId}`);
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-class', classId] });
      queryClient.invalidateQueries({ queryKey: ['school-classes'] });
    },
  });

  const enrollMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const response = await api.post(`/school/classes/${classId}/enroll`, { memberId });
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-class', classId] });
      queryClient.invalidateQueries({ queryKey: ['school-classes'] });
      setShowEnrollStudent(false);
      setStudentSearch('');
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: async (enrollmentId: string) => {
      const response = await api.patch(`/school/enrollment/${enrollmentId}`, { status: 'withdrawn' });
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-class', classId] });
      queryClient.invalidateQueries({ queryKey: ['school-classes'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!classDetail) return null;

  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-200 p-6 space-y-6">
      {/* Class Info */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{classDetail.name}</h3>
        <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600">
          <span className="flex items-center gap-1"><Calendar className="h-4 w-4" /> {DAY_NAMES[classDetail.dayOfWeek]}</span>
          <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {classDetail.startTime} - {classDetail.endTime}</span>
          {classDetail.room && <span>Room: {classDetail.room}</span>}
          {classDetail.gradeLevel && <span>Grade: {classDetail.gradeLevel}</span>}
        </div>
        {classDetail.description && <p className="mt-2 text-sm text-gray-500">{classDetail.description}</p>}
      </div>

      {/* Teachers */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">Teachers</h4>
          <button
            onClick={() => setShowAddTeacher(!showAddTeacher)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {showAddTeacher ? 'Cancel' : '+ Add Teacher'}
          </button>
        </div>

        {showAddTeacher && (
          <div className="mb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={teacherSearch}
                onChange={(e) => setTeacherSearch(e.target.value)}
                placeholder="Search members..."
                className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            {teacherResults && teacherResults.length > 0 && (
              <div className="mt-1 border border-gray-200 rounded-lg max-h-32 overflow-y-auto">
                {teacherResults.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => addTeacherMutation.mutate(m.id)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                  >
                    {m.firstName} {m.lastName}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="space-y-2">
          {classDetail.teachers.map((t) => (
            <div key={t.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
              <div>
                <span className="text-sm font-medium text-gray-900">{t.memberName || 'Unknown'}</span>
                <span className="ml-2 text-xs text-gray-500">({t.role})</span>
              </div>
              <button
                onClick={() => removeTeacherMutation.mutate(t.id)}
                className="p-1 text-gray-400 hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {classDetail.teachers.length === 0 && (
            <p className="text-sm text-gray-500">No teachers assigned yet.</p>
          )}
        </div>
      </div>

      {/* Roster */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">
            Roster ({classDetail.enrollmentCount}{classDetail.maxStudents ? `/${classDetail.maxStudents}` : ''})
          </h4>
          <button
            onClick={() => setShowEnrollStudent(!showEnrollStudent)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {showEnrollStudent ? 'Cancel' : '+ Enroll Student'}
          </button>
        </div>

        {showEnrollStudent && (
          <div className="mb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                placeholder="Search members..."
                className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            {studentResults && studentResults.length > 0 && (
              <div className="mt-1 border border-gray-200 rounded-lg max-h-32 overflow-y-auto">
                {studentResults.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => enrollMutation.mutate(m.id)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                  >
                    {m.firstName} {m.lastName}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="space-y-2">
          {classDetail.enrollments
            .filter((e) => e.status === 'enrolled')
            .map((enrollment) => (
              <div key={enrollment.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                <span className="text-sm font-medium text-gray-900">{enrollment.memberName || 'Unknown'}</span>
                <button
                  onClick={() => withdrawMutation.mutate(enrollment.id)}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-600"
                  title="Withdraw"
                >
                  <UserMinus className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          {classDetail.enrollments.filter((e) => e.status === 'enrolled').length === 0 && (
            <p className="text-sm text-gray-500">No students enrolled yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== Attendance Tab =====

function AttendanceTab() {
  const queryClient = useQueryClient();
  const [selectedClassId, setSelectedClassId] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceState, setAttendanceState] = useState<
    Record<string, { status: string; notes: string }>
  >({});
  const [saving, setSaving] = useState(false);

  const { data: classes } = useQuery({
    queryKey: ['school-classes-all'],
    queryFn: async () => {
      const response = await api.get<SchoolClass[]>('/school/classes');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const { data: enrollment } = useQuery({
    queryKey: ['class-enrollment', selectedClassId],
    queryFn: async () => {
      const response = await api.get<Enrollment[]>(`/school/classes/${selectedClassId}/enrollment`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
    enabled: !!selectedClassId,
  });

  const { data: existingAttendance } = useQuery({
    queryKey: ['class-attendance', selectedClassId, currentDate],
    queryFn: async () => {
      const response = await api.get<AttendanceRecord[]>(
        `/school/classes/${selectedClassId}/attendance?from=${currentDate}&to=${currentDate}`
      );
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
    enabled: !!selectedClassId,
  });

  // Initialize attendance state from existing records
  const enrolledStudents = enrollment?.filter((e) => e.status === 'enrolled') || [];

  const getAttendanceStatus = (memberId: string): string => {
    if (attendanceState[memberId]) return attendanceState[memberId].status;
    const existing = existingAttendance?.find((a) => a.memberId === memberId);
    return existing?.status || 'present';
  };

  const getAttendanceNotes = (memberId: string): string => {
    if (attendanceState[memberId]) return attendanceState[memberId].notes;
    const existing = existingAttendance?.find((a) => a.memberId === memberId);
    return existing?.notes || '';
  };

  const setStatus = (memberId: string, status: string) => {
    setAttendanceState((prev) => ({
      ...prev,
      [memberId]: { status, notes: prev[memberId]?.notes || getAttendanceNotes(memberId) },
    }));
  };

  const setNotes = (memberId: string, notes: string) => {
    setAttendanceState((prev) => ({
      ...prev,
      [memberId]: { status: prev[memberId]?.status || getAttendanceStatus(memberId), notes },
    }));
  };

  const saveAttendance = async () => {
    if (!selectedClassId || enrolledStudents.length === 0) return;

    setSaving(true);
    try {
      const records = enrolledStudents.map((e) => ({
        memberId: e.memberId,
        status: getAttendanceStatus(e.memberId) as 'present' | 'absent' | 'late' | 'excused',
        notes: getAttendanceNotes(e.memberId) || undefined,
      }));

      const response = await api.post(`/school/classes/${selectedClassId}/attendance`, {
        date: currentDate,
        records,
      });

      if (!response.success) throw new Error(response.error?.message);

      queryClient.invalidateQueries({ queryKey: ['class-attendance', selectedClassId, currentDate] });
      setAttendanceState({});
    } catch (err) {
      // Error handling
    } finally {
      setSaving(false);
    }
  };

  const changeDate = (days: number) => {
    const date = new Date(currentDate);
    date.setDate(date.getDate() + days);
    setCurrentDate(date.toISOString().split('T')[0]);
    setAttendanceState({});
  };

  const statusOptions = [
    { value: 'present', label: 'Present', icon: Check, color: 'text-green-600 bg-green-50 border-green-200' },
    { value: 'absent', label: 'Absent', icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200' },
    { value: 'late', label: 'Late', icon: Clock, color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
    { value: 'excused', label: 'Excused', icon: FileText, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  ];

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <select
            value={selectedClassId}
            onChange={(e) => { setSelectedClassId(e.target.value); setAttendanceState({}); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">Select a class...</option>
            {classes?.map((c) => (
              <option key={c.id} value={c.id}>{c.name} - {DAY_NAMES[c.dayOfWeek]}</option>
            ))}
          </select>

          {selectedClassId && (
            <div className="flex items-center gap-2">
              <button onClick={() => changeDate(-1)} className="p-1 text-gray-400 hover:text-gray-600">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <input
                type="date"
                value={currentDate}
                onChange={(e) => { setCurrentDate(e.target.value); setAttendanceState({}); }}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <button onClick={() => changeDate(1)} className="p-1 text-gray-400 hover:text-gray-600">
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        {selectedClassId && enrolledStudents.length > 0 && (
          <button
            onClick={saveAttendance}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Save Attendance
          </button>
        )}
      </div>

      {/* Attendance Grid */}
      {selectedClassId && (
        <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
          {enrolledStudents.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              No students enrolled in this class.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {enrolledStudents.map((e) => {
                  const memberName = e.member ? `${e.member.firstName} ${e.member.lastName}` : 'Unknown';
                  const currentStatus = getAttendanceStatus(e.memberId);
                  const currentNotes = getAttendanceNotes(e.memberId);

                  return (
                    <tr key={e.id}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{memberName}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {statusOptions.map((opt) => (
                            <button
                              key={opt.value}
                              onClick={() => setStatus(e.memberId, opt.value)}
                              className={cn(
                                'px-2 py-1 rounded-md text-xs font-medium border transition-colors',
                                currentStatus === opt.value
                                  ? opt.color
                                  : 'text-gray-400 bg-white border-gray-200 hover:border-gray-300'
                              )}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={currentNotes}
                          onChange={(ev) => setNotes(e.memberId, ev.target.value)}
                          placeholder="Optional notes..."
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!selectedClassId && (
        <div className="text-center py-12 text-gray-500">
          Select a class to take attendance
        </div>
      )}
    </div>
  );
}

// ===== Reports Tab =====

function ReportsTab() {
  const [selectedClassId, setSelectedClassId] = useState('');

  const { data: classes } = useQuery({
    queryKey: ['school-classes-all'],
    queryFn: async () => {
      const response = await api.get<SchoolClass[]>('/school/classes');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const { data: report } = useQuery({
    queryKey: ['attendance-report', selectedClassId],
    queryFn: async () => {
      const response = await api.get<AttendanceReport>(`/school/classes/${selectedClassId}/attendance/report`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    enabled: !!selectedClassId,
  });

  const overallAttendance = report?.students && report.students.length > 0
    ? Math.round(report.students.reduce((sum, s) => sum + s.attendancePercentage, 0) / report.students.length)
    : 0;

  const getAttendanceColor = (percentage: number) => {
    if (percentage < 50) return 'text-red-600 bg-red-50';
    if (percentage < 75) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <select
          value={selectedClassId}
          onChange={(e) => setSelectedClassId(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Select a class...</option>
          {classes?.map((c) => (
            <option key={c.id} value={c.id}>{c.name} - {DAY_NAMES[c.dayOfWeek]}</option>
          ))}
        </select>
      </div>

      {report && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-white border border-gray-200 p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{report.totalClasses}</p>
              <p className="text-sm text-gray-500">Total Classes</p>
            </div>
            <div className={cn('rounded-lg border p-4 text-center', getAttendanceColor(overallAttendance))}>
              <p className="text-2xl font-bold">{overallAttendance}%</p>
              <p className="text-sm">Overall Attendance</p>
            </div>
          </div>

          {/* Student Report */}
          <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Present</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Absent</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Late</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Excused</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Attendance %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {report.students.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      No attendance data available.
                    </td>
                  </tr>
                ) : (
                  report.students.map((student) => (
                    <tr key={student.memberId}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{student.memberName || 'Unknown'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{student.totalClasses}</td>
                      <td className="px-4 py-3 text-sm text-green-600 text-center">{student.present}</td>
                      <td className="px-4 py-3 text-sm text-red-600 text-center">{student.absent}</td>
                      <td className="px-4 py-3 text-sm text-yellow-600 text-center">{student.late}</td>
                      <td className="px-4 py-3 text-sm text-blue-600 text-center">{student.excused}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={cn(
                          'inline-flex rounded-full px-2 py-1 text-xs font-medium',
                          getAttendanceColor(student.attendancePercentage)
                        )}>
                          {student.attendancePercentage}%
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!selectedClassId && (
        <div className="text-center py-12 text-gray-500">
          Select a class to view attendance reports
        </div>
      )}
    </div>
  );
}
